/**
 * Роли сотрудников и права — одна таблица на сервер и приложение.
 * Сервер проверяет права сам; приложение по этой же таблице только прячет лишние кнопки.
 *
 * Как добавить роль: допишите её в ROLES, ROLE_LABEL и MATRIX. Миграция не нужна —
 * в базе роль хранится строкой и проверяется здесь.
 */
export const ROLES = ['admin', 'chief', 'master', 'adjuster', 'operator', 'technologist', 'storekeeper', 'qc'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Администратор',
  chief: 'Начальник производства',
  master: 'Мастер смены',
  adjuster: 'Наладчик',
  operator: 'Оператор',
  technologist: 'Технолог',
  storekeeper: 'Кладовщик',
  qc: 'Контролёр ОТК',
};

export const PERMISSIONS = {
  'panel.view': 'Открывать панели оборудования своих участков и управлять ими',
  'panel.manage': 'Добавлять и настраивать панели и участки',
  'panel.log': 'Смотреть журнал: кто подключался и что нажимал',
  'session.terminate': 'Обрывать чужие подключения к панелям',
  'areas.all': 'Видеть оборудование всех участков, а не только своих',
  'staff.manage': 'Добавлять сотрудников, назначать роли и участки',
} as const;
export type Permission = keyof typeof PERMISSIONS;

/**
 * Что видит сотрудник, задают участки (Ещё → Сотрудники → участки); роль — что он может.
 * Открыть панель = управлять ею. Только просмотр — настройка конкретной панели («Разрешить управление»).
 */
const MATRIX: Record<Role, readonly Permission[]> = {
  admin: ['panel.view', 'panel.manage', 'panel.log', 'session.terminate', 'areas.all', 'staff.manage'],
  chief: ['panel.view', 'panel.log', 'session.terminate', 'areas.all'],
  master: ['panel.view', 'panel.log'],
  adjuster: ['panel.view'],
  operator: ['panel.view'],
  technologist: ['panel.view', 'areas.all'],
  storekeeper: [],
  qc: ['panel.view'],
};

export function isRole(v: unknown): v is Role {
  return typeof v === 'string' && (ROLES as readonly string[]).includes(v);
}

export function can(role: Role | null | undefined, perm: Permission): boolean {
  return !!role && MATRIX[role].includes(perm);
}

export function permissionsOf(role: Role): Permission[] {
  return [...MATRIX[role]];
}
